import React, { useState } from "react";

import {
    Badge,
    Box,
    Button,
    Center,
    Flex,
    Heading,
    HStack,
    Icon,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    SimpleGrid,
    Stack,
    StackDivider,
    Text,
    useBreakpointValue,
    VStack
} from "@chakra-ui/react";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaArrowTrendUp } from "react-icons/fa6";

import "react-phone-input-2/lib/style.css";

import { useClientDashboard } from "@/hooks/features/useClientDashboard";
import { useCompanyDashboard } from "@/hooks/features/useCompanyDashboard";
import { useRangeSelector } from "@/hooks/util/useRangeSelector";
import { formatNumberWithThousandsSeparator } from "@/util/helpers";
import { generateTestId } from "@/util/testIds";

import { ClientTransactionReport } from "./ClientTransactionReport";

export interface ClientBillingProps {}

export const ClientBilling: React.FC<ClientBillingProps> = () => {
  const { dateRange, monthsList, setDateRange, handleRangeReset } =
    useRangeSelector();

  const [offset, setOffset] = useState(0);

  const { companyTransactions, isCompanyTransactionsLoading } =
    useCompanyDashboard({
      label: dateRange.label,
      before: dateRange.before,
      after: dateRange.after,
      offset
    });

  const { dashboard: summary } = useClientDashboard(dateRange);

  // Responsive layout configurations
  const isMobile = useBreakpointValue({ base: true, md: false });
  const metricsDirection = useBreakpointValue({ base: "column", lg: "row" }) as "column" | "row";
  const headerDirection = useBreakpointValue({ base: "column", xl: "row" }) as "column" | "row";
  const headerAlign = useBreakpointValue({ base: "flex-start", xl: "center" }) as "flex-start" | "center";
  const headerSpacing = useBreakpointValue({ base: 4, xl: 20 });

  const handlePageChange = (page: number) => {
    setOffset(page * 20);
  };

  return (
    <Box
      pt={10}
      data-testid={generateTestId.section("client-billing-container")}
    >
      <Box
        bg="white"
        rounded={16}
        px={[4, 6, 8]}
        py={8}
        w="full"
        data-testid={generateTestId.section("client-billing-header")}
      >
        <HStack
          spacing={4}
          data-testid={generateTestId.section("billing-title")}
        >
          <Heading
            fontSize={20}
            fontWeight={600}
            data-testid={generateTestId.section("billing-heading")}
          >
            Billing
          </Heading>
        </HStack>
        <Text
          color="secondary.700"
          fontSize={13}
          mt={2}
          fontWeight={500}
          opacity={0.7}
          data-testid={generateTestId.section("billing-description")}
        >
          Shows Jack&apos;s extraction time and cost for all your clients
        </Text>

        <Flex
          align={headerAlign}
          mt={8}
          gap={headerSpacing}
          direction={headerDirection}
          data-testid={generateTestId.section("billing-controls-and-metrics")}
        >
          <VStack
            spacing={1}
            align="flex-start"
            data-testid={generateTestId.section("billing-period-selector")}
          >
            <Text
              fontSize={13}
              data-testid={generateTestId.section("billing-period-label")}
            >
              Billing Period
            </Text>
            <Flex gap={3} direction={isMobile ? "column" : "row"} w="full">
              <Box w={isMobile ? "full" : "auto"}>
                <Menu
                  placement="bottom-start"
                  data-testid={generateTestId.section("billing-period-menu")}
                >
                  <MenuButton
                    variant="outline"
                    as={Button}
                    h={10}
                    w={isMobile ? "full" : "261px"}
                    textAlign="left"
                    size="sm"
                    rightIcon={<ChevronDownIcon />}
                    data-testid={generateTestId.button(
                      "billing-period-menu-button"
                    )}
                  >
                    {dateRange.label}
                  </MenuButton>
                  <MenuList
                    data-testid={generateTestId.section(
                      "billing-period-menu-list"
                    )}
                  >
                    {monthsList.map((month, index) => (
                      <MenuItem
                        fontWeight={500}
                        onClick={() => setDateRange(month)}
                        key={month.label}
                        data-testid={generateTestId.button(
                          "billing-period-option",
                          index.toString()
                        )}
                      >
                        {month.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>

              <Button
                onClick={handleRangeReset}
                variant="link"
                colorScheme="blue"
                size="sm"
                data-testid={generateTestId.button("reset", "billing-period")}
              >
                Reset
              </Button>
            </Flex>
          </VStack>

          <SimpleGrid
            columns={isMobile ? 1 : 2}
            spacing={4}
            w={isMobile ? "full" : "auto"}
            data-testid={generateTestId.section("billing-metrics")}
          >
            <Box
              rounded={8}
              bg="blackAlpha.20"
              py={4}
              px={[4, 6, 8]}
              minW={isMobile ? "full" : "200px"}
              data-testid={generateTestId.widget("processed-invoices")}
            >
              <Text
                fontWeight={500}
                fontSize="sm"
                data-testid={generateTestId.widget("processed-invoices-label")}
              >
                Processed Invoices
              </Text>

              <HStack spacing={4} mt={2}>
                <Text
                  fontSize={[24, 28, 32]}
                  fontWeight={700}
                  color="primary.500"
                  data-testid={generateTestId.widget(
                    "processed-invoices-value"
                  )}
                >
                  {summary ? summary.total_processed : 0}
                </Text>
                <Center
                  boxSize={6}
                  color="green.400"
                  bg="green.50"
                  rounded="md"
                  data-testid={generateTestId.icon("trend-up")}
                >
                  <Icon as={FaArrowTrendUp} />
                </Center>
              </HStack>
            </Box>

            <Box
              rounded={8}
              bg="blackAlpha.20"
              py={4}
              px={[4, 6, 8]}
              minW={isMobile ? "full" : "200px"}
              data-testid={generateTestId.widget("billable-amount")}
            >
              <Text
                fontWeight={500}
                fontSize="sm"
                data-testid={generateTestId.widget("billable-amount-label")}
              >
                Billable Amount
              </Text>

              <HStack align="baseline" spacing={2} mt={2}>
                <Text
                  fontSize={[24, 28, 32]}
                  fontWeight={700}
                  color="primary.500"
                  data-testid={generateTestId.widget("billable-amount-value")}
                >
                  £
                  {summary?.billable_amount
                    ? formatNumberWithThousandsSeparator(
                        Number(summary.billable_amount)
                      )
                    : 0}
                </Text>
                <Text
                  fontWeight={500}
                  fontSize="sm"
                  data-testid={generateTestId.widget(
                    "billable-amount-currency"
                  )}
                >
                  GBP
                </Text>
              </HStack>
            </Box>
          </SimpleGrid>
        </Flex>
      </Box>

      <Stack
        mt={7}
        bg="white"
        w="full"
        rounded={6}
        border="1px solid"
        spacing={4}
        borderColor="blackAlpha.200"
        divider={<StackDivider />}
        py={6}
        px={[4, 6, 10]}
        data-testid={generateTestId.section("all-transactions-section")}
      >
        <Flex
          justify="space-between"
          align={isMobile ? "flex-start" : "center"}
          direction={isMobile ? "column" : "row"}
          gap={isMobile ? 2 : 0}
          data-testid={generateTestId.section("all-transactions-header")}
        >
          <Text
            fontWeight={500}
            fontSize="sm"
            data-testid={generateTestId.section("all-transactions-title")}
          >
            All Transactions
          </Text>

          <Badge
            color="primary.500"
            bg="primary.50"
            fontWeight={500}
            textTransform="initial"
            py={1}
            px={2}
            data-testid={generateTestId.widget("date-range-badge")}
          >
            {dateRange.label}
          </Badge>
        </Flex>

        <ClientTransactionReport
          reports={
            companyTransactions || { total: 0, limit: 0, offset: 0, items: [] }
          }
          isClientTransactionsLoading={isCompanyTransactionsLoading}
          onPageChange={handlePageChange}
          data-testid={generateTestId.widget("client-transaction-report")}
        />
      </Stack>
    </Box>
  );
};
